import axios from 'axios';
import { createApiClient } from '@shared/api-client';
import type { BulkBookingItem as ApiBulkBookingItem } from '@shared/api-client';
import { AUTH_TOKEN, TALLINN_TIMEZONE } from '@shared/constants';
import { User, UserProfile } from '@shared/types';
import { formatInTimeZone } from 'date-fns-tz';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Constructs the full URL for any media asset (image, video, flag, etc.).
 * Absolute URLs are returned unchanged.
 * Paths under /uploads/ were written by the API server and get the API base URL prepended.
 * All other relative paths are portal-hosted and returned as-is (relative), so the
 * browser resolves them against the portal's own origin.
 *
 * Portal-served path prefixes:
 *   /images/  – portal/public/images/   (served by CRA / web server from public/)
 *   /flags/   – portal/public/flags/    (served by CRA / web server from public/)
 *   /assets/  – portal/assets/          (served by setupProxy.js in dev;
 *                                         by the web server rewrite rule in prod)
 *   /videos/  – portal/assets/videos/   (same as /assets/ above)
 */
export function getAssetUrl(url: string): string {
  if (!url) return '';
  // If it's already an absolute URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Portal static files (public/ folder) - keep relative
  if (
    url.startsWith('/images/') ||
    url.startsWith('/assets/') ||
    url.startsWith('/videos/') ||
    url.startsWith('/flags/')
  ) {
    return url;
  }
  // API uploads - prepend API base URL
  return `${API_BASE_URL}${url}`;
}

// Create the portal-specific axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token (with impersonation support)
axiosInstance.interceptors.request.use(
  (config) => {
    // Check for impersonation token first
    const impersonationData = localStorage.getItem('impersonation_data');
    if (impersonationData) {
      try {
        const parsed = JSON.parse(impersonationData);
        // Check if impersonation is still valid
        if (new Date(parsed.expiresAt) > new Date()) {
          config.headers.Authorization = `Impersonate ${parsed.token}`;
          return config;
        }
      } catch (e) {
        // Invalid impersonation data, proceed with normal auth
      }
    }

    // Fall back to regular Auth0 token
    const token = localStorage.getItem(AUTH_TOKEN.PORTAL);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Create the shared API client
const apiClient = createApiClient(axiosInstance);

// Export axios instance for endpoints not yet in shared client (invitations, services-meta, etc.)
export const axiosClient = axiosInstance;

// Session Bundle endpoints (using shared client)
export const sessionBundleAPI = {
  getAll: (code?: string) => apiClient.getSessionBundles(code).then((data) => ({ data })),
  getById: (id: string) => apiClient.getSessionBundle(id).then((data) => ({ data })),
};

// Packages endpoints (using shared client)
export const packageAPI = {
  getAll: () => apiClient.getPackages().then((data) => ({ data })),
  getById: (id: string) => apiClient.getPackage(id).then((data) => ({ data })),
};

// Package Packs endpoints (using shared client)
export const packagePacksAPI = {
  getById: (id: string, code?: string) =>
    apiClient.getPackagePack(id, code).then((data) => ({ data })),
};

// Service endpoints (using shared client)
export const serviceAPI = {
  getAll: () => apiClient.getServices().then((data) => ({ data })),
  getById: (id: string) => apiClient.getService(id).then((data) => ({ data })),
  getAvailability: (id: string, date: string) =>
    apiClient.getServiceAvailability(id, { date }).then((data) => ({ data })),
};

// Booking endpoints (using shared client)
export const bookingAPI = {
  getAll: (params?: {
    from?: string;
    to?: string;
    offset?: number;
    limit?: number;
    serviceID?: string;
    status?: string;
    include?: string;
    populate?: string;
  }) => apiClient.getBookings(params).then((data) => ({ data })),
  getByOrderId: (orderId: string) =>
    axiosInstance
      .get(`/api/bookings/order/${orderId}`)
      .then((response) => ({ data: response.data })),

  createBulk: (data: {
    items: Array<Omit<ApiBulkBookingItem, 'date' | 'time' | 'phoneNumber'>>;
    paymentDetails?: {
      amount?: number;
      customerId?: string;
      paymentMethodId?: string;
      paymentIntentId?: string;
    };
    userSessionBundleId?: string;
    paymentMethod?: string;
    selectedSessionBundleId?: string;
    giftCardCode?: string;
    couponCode?: string;
    promoCode?: string;
    language?: string;
    guestName?: string;
    guestEmail?: string;
    phoneNumber?: string;
  }) => {
    const transformedItems = data.items.map((item) => {
      const startDateTime = new Date(item.startTime);
      // Calculate participants: 1 (self) + number of invitees
      const participants = 1 + (item.invitees?.length || 0);

      return {
        cartItemId: item.cartItemId,
        serviceId: item.serviceId,
        startTime: item.startTime,
        date: formatInTimeZone(startDateTime, TALLINN_TIMEZONE, 'yyyy-MM-dd'),
        time: formatInTimeZone(startDateTime, TALLINN_TIMEZONE, 'HH:mm'),
        duration: item.duration,
        price: item.price,
        phoneNumber: data.phoneNumber || '',
        participants,
        ...(item.isPackageService !== undefined ? { isPackageService: item.isPackageService } : {}),
        ...(item.packageId ? { packageId: item.packageId } : {}),
        ...(item.packageName ? { packageName: item.packageName } : {}),
        ...(item.packageNameEn ? { packageNameEn: item.packageNameEn } : {}),
        ...(item.packageGroupId ? { packageGroupId: item.packageGroupId } : {}),
        // Include invitees if they exist (filter out empty strings)
        ...(item.invitees && item.invitees.length > 0
          ? { invitees: item.invitees.filter((email) => email && email.trim()) }
          : {}),
      };
    });

    return apiClient
      .createBulkBookings({
        items: transformedItems,
        paymentDetails: data.paymentDetails,
        paymentMethod: data.paymentMethod,
        userSessionBundleId: data.userSessionBundleId,
        selectedSessionBundleId: data.selectedSessionBundleId,
        giftCardCode: data.giftCardCode,
        couponCode: data.couponCode,
        promoCode: data.promoCode,
        language: data.language,
        guestName: data.guestName,
        guestEmail: data.guestEmail,
        phoneNumber: data.phoneNumber,
      })
      .then((result) => ({ data: result }));
  },
  cancel: (id: string, language?: string) =>
    apiClient.cancelBooking(id, { language }).then(() => ({ data: {} })),
  reschedule: (
    id: string,
    data: {
      date: string;
      time: string;
      language?: string;
    }
  ) => apiClient.rescheduleBooking(id, data).then((result) => ({ data: result })),
  /**
   * Get coupon pair status for a booking
   * Returns information about whether this booking is part of a coupon pair and cancellation eligibility
   */
  getCouponStatus: (id: string) =>
    axiosInstance
      .get(`/api/bookings/${id}/coupon-status`)
      .then((response) => ({ data: response.data })),
  /**
   * Cancel both linked coupon sessions together
   * This is used when a booking is part of a coupon pair and needs to be cancelled with its linked session
   */
  cancelCouponPair: (id: string, language?: string) =>
    axiosInstance
      .post(`/api/bookings/${id}/cancel-coupon-pair`, { language })
      .then((response) => ({ data: response.data })),
};

// Gift Card endpoints (using shared client)
export const giftCardAPI = {
  create: (data: {
    amount: number;
    recipient_name: string;
    recipient_email: string;
    sender_name: string;
    sender_email: string;
    message?: string;
  }) => apiClient.createGiftCard(data).then((result) => ({ data: result })),
  getBalance: (code: string) =>
    apiClient.getGiftCardBalance(code).then((result) => ({ data: result })),
  redeem: (code: string, amount: number) =>
    apiClient.redeemGiftCard({ code, amount_to_redeem: amount }).then(() => ({ data: {} })),
  createPaymentIntent: (amount: number) =>
    apiClient.purchaseGiftCard(amount).then((result) => ({ data: result })),
  confirmPurchase: (data: {
    paymentIntentId: string;
    amount: number;
    senderName: string;
    senderEmail: string;
    recipientName: string;
    recipientEmail: string;
    message?: string;
  }) => apiClient.confirmGiftCardPurchase(data).then((result) => ({ data: result })),
};

export const promotionAPI = {
  validate: (code: string) =>
    apiClient.validatePromotionCode(code).then((result) => ({ data: result })),
};

// Payment endpoints (using shared client)
export const paymentAPI = {
  createPaymentIntent: (data: {
    sessionBundleId?: string;
    packagePackId?: string;
    giftCardCode?: string;
    promoCode?: string | null;
  }) => apiClient.createPaymentIntent(data).then((result) => ({ data: result })),
  createSubscriptionIntent: (data: {
    subscriptionId: string;
    giftCardCode?: string;
    promoCode?: string | null;
  }) => apiClient.createSubscriptionIntent(data).then((result) => ({ data: result })),
  confirmSubscription: (data: { subscriptionId: string; paymentMethodId: string }) =>
    apiClient.confirmSubscription(data).then(() => ({ data: {} })),
  createBookingPaymentIntent: (data: { type: 'service' | 'package'; id: string; price: number }) =>
    apiClient.createBookingPaymentIntent(data).then((result) => ({ data: result })),
  createContractAfterPayment: (data: {
    paymentIntentId: string;
    itemType?: 'subscription' | 'session_bundle' | 'package_pack';
    itemId?: string;
    sessionBundleId?: string;
  }) => apiClient.createContract(data).then((result) => ({ data: result })),
};

// User endpoints (using shared client)
export const userAPI = {
  getUserSessionBundles: () => apiClient.getUserSessionBundles().then((data) => ({ data })),
  getPackagePacks: () => apiClient.getUserPackagePacks().then((data) => ({ data })),
  cancelSubscription: (
    subscriptionId: string
  ): Promise<{ data: { message: string; ends_at: string } }> =>
    apiClient.cancelSubscription(subscriptionId).then((data) => ({ data })),
  getSessionBundles: () => apiClient.getUserSessionBundles().then((data) => ({ data })),
  getSubscriptions: () => apiClient.getUserSubscriptions().then((data) => ({ data })),
  getMe: (): Promise<{ data: User }> => apiClient.getCurrentUser().then((data) => ({ data })),
  getProfile: (): Promise<{ data: UserProfile }> =>
    apiClient.getUserProfile().then((data) => ({ data })),
  updateProfile: (data: {
    phoneNumber?: string;
    spouseEmail?: string;
    name?: string;
    email?: string;
    promoPreference?: boolean;
    gender?: 'male' | 'female' | null;
    birthday?: string | null;
  }) => apiClient.updateUserProfile(data).then((result) => ({ data: result })),
  completeRegistration: (data: {
    name: string;
    termsAccepted: boolean;
    promotionalEmails: boolean;
    promoPreference?: boolean;
    gender?: 'male' | 'female';
    birthday?: string;
  }) => apiClient.completeRegistration(data).then(() => ({ data: {} })),
  verifyEmail: (token: string) => {
    console.log('[USER-API] verifyEmail called with token:', {
      token,
      tokenLength: token?.length || 0,
    });
    return apiClient.verifyEmail({ token }).then(() => ({ data: {} }));
  },
  getSpouseMaxPrice: (): Promise<{
    data: {
      maxSpousePrice: number | null;
      activeProductsCount?: number;
      isInviteeSpouse?: boolean;
      message?: string;
    };
  }> =>
    axiosInstance
      .get('/api/users/me/spouse-max-price')
      .then((response) => ({ data: response.data })),
  updateUserPreferences: (prefs: { notifyByEmail: boolean; notifyBySms: boolean }) => {
    return axiosInstance.put('/api/users/me/preferences', prefs).then((res) => res.data);
  },
};

// Coupon Code endpoints
export const couponCodeAPI = {
  /**
   * Validate a coupon code for a given email and cart
   * @param code - The coupon code to validate
   * @param email - User's email address
   * @param sessionsInCart - Number of sessions in the cart
   * @param language - Language for messages (en, et, fi, ru)
   * @param cartPrices - Array of session prices in cents (one per session in cart)
   */
  validate: (
    code: string,
    email: string,
    sessionsInCart: number,
    language: string = 'en',
    cartPrices?: number[]
  ) =>
    axiosInstance
      .post('/api/coupon-codes/validate', {
        code,
        email,
        sessionsInCart,
        language,
        cartPrices,
      })
      .then((response) => ({ data: response.data })),

  /**
   * Quick check if a coupon code exists and is active (without email validation)
   * @param code - The coupon code to check
   */
  check: (code: string) =>
    axiosInstance
      .get(`/api/coupon-codes/check/${encodeURIComponent(code)}`)
      .then((response) => ({ data: response.data })),
};

// Export convenience functions for backwards compatibility
export const getGiftCardBalance = (code: string) => giftCardAPI.getBalance(code);
export const redeemGiftCard = (code: string, amount: number) => giftCardAPI.redeem(code, amount);

// Service time endpoints (now using local database via backend API)
export const serviceTimeAPI = {
  getAvailableTimes: (serviceId: string, date: string, resourceId?: string) =>
    apiClient.getServiceTimes(serviceId, { date, resourceId }).then((data) => ({ data })),
};

export const getAvailabilityDates = (serviceId: string, startDate: string, endDate: string) => {
  return axiosInstance.get('/api/v1/bookla/services/' + serviceId + '/availability-dates', {
    params: { startDate, endDate },
  });
};

// Unified API client with convenience methods (for backwards compatibility)
const unifiedAPI = {
  ...axiosInstance,
  getUserSessionBundles: () => apiClient.getUserSessionBundles(),
  cancelSubscription: (subscriptionId: string) => apiClient.cancelSubscription(subscriptionId),
  getMe: () => apiClient.getCurrentUser(),
  getProfile: () => apiClient.getUserProfile(),
  updateProfile: (data: Partial<UserProfile>) => apiClient.updateUserProfile(data),
  completeRegistration: (data: {
    name: string;
    termsAccepted: boolean;
    promotionalEmails: boolean;
    promoPreference?: boolean;
    gender?: 'male' | 'female';
    birthday?: string;
  }) => apiClient.completeRegistration(data),
};

// Gallery types
export interface GalleryImage {
  id: string;
  image_url: string;
  thumbnail_url?: string;
  lightbox_url?: string;
  descriptions: {
    en?: string;
    et?: string;
    fi?: string;
    ru?: string;
  };
  sort_order: number;
}

export interface Gallery {
  id: string;
  name: string;
  type: 'carousel' | 'grid';
  description: string | null;
  images: GalleryImage[];
}

// Gallery endpoints
export const galleryAPI = {
  /**
   * Get all active galleries
   */
  getAll: () =>
    axiosInstance.get<Gallery[]>('/api/galleries').then((response) => ({ data: response.data })),

  /**
   * Get gallery by type (carousel or grid) with images
   */
  getByType: (type: 'carousel' | 'grid') =>
    axiosInstance
      .get<Gallery>(`/api/galleries/type/${type}`)
      .then((response) => ({ data: response.data })),

  /**
   * Get gallery by ID with images
   */
  getById: (id: string) =>
    axiosInstance
      .get<Gallery>(`/api/galleries/${id}`)
      .then((response) => ({ data: response.data })),
};

// Export convenience functions for backwards compatibility
export const getAvailableTimes = (serviceId: string, startDate: string, endDate: string) =>
  serviceTimeAPI.getAvailableTimes(serviceId, startDate);

export default unifiedAPI;
